import { SportId } from 'app/lib/betting/constants';

import {
  BASKETBALL_FIRST_HALF_LINE_ID,
  BASKETBALL_FIRST_QUARTER_LINE_ID,
  BASKETBALL_FIRST_QUARTER_THREE_POINTS_LINE_ID,
  BASKETBALL_MAIN_LINE_ID,
  ICE_HOCKEY_FIRST_PERIOD_LINE_ID,
  ICE_HOCKEY_MAIN_LINE_ID,
  ICE_HOCKEY_SECOND_PERIOD_LINE_ID,
  SOCCER_CORNER_KICKS_LINE_ID,
  SOCCER_FIRST_HALF_LINE_ID,
  SOCCER_MAIN_LINE_ID,
  SOCCER_SECOND_HALF_LINE_ID,
  SOCCER_YELLOW_CARD_LINE_ID,
  TENNIS_FIFTH_SET_LINE_ID,
  TENNIS_FIRST_SET_LINE_ID,
  TENNIS_FOURTH_SET_LINE_ID,
  TENNIS_MAIN_LINE_ID,
  TENNIS_SECOND_SET_LINE_ID,
  TENNIS_THIRD_SET_LINE_ID,
  VOLLEYBALL_ACE_LINE_ID,
  VOLLEYBALL_FIRST_SET_LINE_ID,
  VOLLEYBALL_MAIN_LINE_ID,
  VOLLEYBALL_SECOND_SET_LINE_ID,
} from '../constants/Grid';
import {
  BasketballPeriodType, BasketballScoreType,
  IceHockeyPeriodType, IceHockeyScoreType,
  SoccerPeriodType, SoccerScoreType,
  TennisPeriodType, TennisScoreType,
  VolleyballPeriodType, VolleyballScoreType,
} from '../lib/betting/betting-entities';
import { $global, createCache } from './$global';

// --

globalThis.INNER_LINE_SCORES_CONF = {
  [SportId.SOCCER]: {
    [SOCCER_MAIN_LINE_ID]: {
      scoreType: SoccerScoreType.GOAL,
      periodType: SoccerPeriodType.MATCH,
      periodNumber: 0,
    },
    [SOCCER_FIRST_HALF_LINE_ID]: {
      scoreType: SoccerScoreType.GOAL,
      periodType: SoccerPeriodType.TIME,
      periodNumber: 1,
    },
    [SOCCER_SECOND_HALF_LINE_ID]: {
      scoreType: SoccerScoreType.GOAL,
      periodType: SoccerPeriodType.TIME,
      periodNumber: 2,
    },
    [SOCCER_CORNER_KICKS_LINE_ID]: {
      scoreType: SoccerScoreType.CORNER,
      periodType: SoccerPeriodType.MATCH,
      periodNumber: 0,
    },
    [SOCCER_YELLOW_CARD_LINE_ID]: {
      scoreType: SoccerScoreType.YELLOW_CARD,
      periodType: SoccerPeriodType.TIME,
      periodNumber: 0,
    },
  },
  [SportId.TENNIS]: {
    [TENNIS_MAIN_LINE_ID]: {
      scoreType: TennisScoreType.POINT,
      periodType: TennisPeriodType.MATCH,
      periodNumber: 0,
    },
    [TENNIS_FIRST_SET_LINE_ID]: {
      scoreType: TennisScoreType.POINT,
      periodType: TennisPeriodType.SET,
      periodNumber: 1,
    },
    [TENNIS_SECOND_SET_LINE_ID]: {
      scoreType: TennisScoreType.POINT,
      periodType: TennisPeriodType.SET,
      periodNumber: 2,
    },
    [TENNIS_THIRD_SET_LINE_ID]: {
      scoreType: TennisScoreType.POINT,
      periodType: TennisPeriodType.SET,
      periodNumber: 3,
    },
    [TENNIS_FOURTH_SET_LINE_ID]: {
      scoreType: TennisScoreType.POINT,
      periodType: TennisPeriodType.SET,
      periodNumber: 4,
    },
    [TENNIS_FIFTH_SET_LINE_ID]: {
      scoreType: TennisScoreType.POINT,
      periodType: TennisPeriodType.SET,
      periodNumber: 5,
    },
  },
  [SportId.BASKETBALL]: {
    [BASKETBALL_MAIN_LINE_ID]: {
      scoreType: BasketballScoreType.POINT,
      periodType: BasketballPeriodType.MATCH,
      periodNumber: 0,
    },
    [BASKETBALL_FIRST_HALF_LINE_ID]: {
      scoreType: BasketballScoreType.POINT,
      periodType: BasketballPeriodType.HALF,
      periodNumber: 1,
    },
    [BASKETBALL_FIRST_QUARTER_LINE_ID]: {
      scoreType: BasketballScoreType.POINT,
      periodType: BasketballPeriodType.QUARTER,
      periodNumber: 1,
    },
    [BASKETBALL_FIRST_QUARTER_THREE_POINTS_LINE_ID]: {
      scoreType: BasketballScoreType.THROW_TRIPLE_POINT,
      periodType: BasketballPeriodType.QUARTER,
      periodNumber: 1,
    },
  },
  [SportId.VOLLEYBALL]: {
    [VOLLEYBALL_MAIN_LINE_ID]: {
      scoreType: VolleyballScoreType.POINT,
      periodType: VolleyballPeriodType.MATCH,
      periodNumber: 0,
    },
    [VOLLEYBALL_FIRST_SET_LINE_ID]: {
      scoreType: VolleyballScoreType.POINT,
      periodType: VolleyballPeriodType.SET,
      periodNumber: 1,
    },
    [VOLLEYBALL_SECOND_SET_LINE_ID]: {
      scoreType: VolleyballScoreType.POINT,
      periodType: VolleyballPeriodType.SET,
      periodNumber: 2,
    },
    [VOLLEYBALL_ACE_LINE_ID]: {
      scoreType: VolleyballScoreType.ACE,
      periodType: VolleyballPeriodType.MATCH,
      periodNumber: 0,
    },
  },
  [SportId.ICE_HOCKEY]: {
    [ICE_HOCKEY_MAIN_LINE_ID]: {
      scoreType: IceHockeyScoreType.GOAL,
      periodType: IceHockeyPeriodType.MATCH,
      periodNumber: 0,
    },
    [ICE_HOCKEY_FIRST_PERIOD_LINE_ID]: {
      scoreType: IceHockeyScoreType.GOAL,
      periodType: IceHockeyPeriodType.PERIOD,
      periodNumber: 1,
    },
    [ICE_HOCKEY_SECOND_PERIOD_LINE_ID]: {
      scoreType: IceHockeyScoreType.GOAL,
      periodType: IceHockeyPeriodType.PERIOD,
      periodNumber: 2,
    },
  },
};

// --

const $cache = createCache('getInnerLineScoresConf');

/**
 * @typedef {object} InnerLineScoresConf
 * @property {number} scoreType
 * @property {number} periodType
 * @property {number} periodNumber
 */

/**
 * Get inner line score types for a given sport and line ids.
 *
 * @param {number} sportId
 * @param {number} lineId
 * @return {InnerLineScoresConf}
 */
export function getInnerLineScoresConf(sportId, lineId) {
  const $innerLineScoresConf = $cache('inner-line-scores');

  if (!(sportId in $innerLineScoresConf)) {
    $innerLineScoresConf[sportId] = $global('INNER_LINE_SCORES_CONF')[sportId] || {};
  }

  if (!(lineId in $innerLineScoresConf[sportId])) {
    $innerLineScoresConf[sportId][lineId] = {};
  }

  return $innerLineScoresConf[sportId][lineId];
}
